<template>
  <b-row class="align-items-center">
    <b-card style="width: 100%" no-body class="mb-1 border-0">
      <b-card-header
        header-tag="header"
        class="py-3 px-4 pointer border-0"
        role="tab"
        v-b-toggle="`${serviceAccount.id}`"
      >
        <div class="d-flex flex-row justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <div>
              <i class="fas fa-sort-down mr-3" />
              <span>{{ serviceAccount.title }}</span>
            </div>
          </div>

          <div>
            <b-badge
              v-if="serviceAccount.full_access"
              :variant="'warning'"
              class="ml-2 font-weight-light"
              style="font-size: 12px"
            >
              {{ $t('resources.service_accounts.full_access') }}
            </b-badge>
            <b-badge
              v-if="serviceAccount.suspended"
              :variant="'danger'"
              class="ml-2 font-weight-light"
              style="font-size: 12px"
            >
              {{ $t('resources.service_accounts.suspended') }}
            </b-badge>
            <b-badge
              :variant="serviceAccount.active ? 'success' : 'info'"
              class="text-white ml-2 font-weight-light"
              style="font-size: 12px"
            >
              {{ serviceAccount.active ? $t('global.active') : $t('global.not_active') }}
            </b-badge>
          </div>
        </div>
      </b-card-header>

      <b-collapse
        :visible="serviceAccount.api_key == ''"
        :id="`${serviceAccount.id}`"
        accordion="my-accordion"
        role="tabpanel"
      >
        <b-card-body class="px-4 pb-0">
          <ResourcesView :block="fields.serviceAccountSettings" :prefill-params="serviceAccount" />

          <div v-if="!serviceAccount.full_access">
            <b-row>
              <b-col>
                <label>Controller</label>
                <multiselect
                  id="controller"
                  v-model="chosenController"
                  select-label=""
                  selected-label=""
                  deselect-label="X"
                  :options="availableControllers"
                  :multiple="false"
                  @input="
                    (value) => {
                      this.chosenAction = '';
                    }
                  "
                >
                  <template slot="noResult">
                    {{ $t('global.no_results') }}
                  </template>
                </multiselect>
              </b-col>
              <b-col>
                <label>Action</label>
                <multiselect
                  :options="availableActions"
                  v-model="chosenAction"
                  id="action"
                  select-label=""
                  selected-label=""
                  deselect-label="X"
                  :disabled="chosenController ? false : true"
                >
                  <template slot="noResult">
                    {{ $t('global.no_results') }}
                  </template>
                </multiselect>
              </b-col>
              <b-col md="10%" class="align-self-end mr-3">
                <b-button
                  :disabled="chosenController && chosenAction ? false : true"
                  @click="addAcl"
                  variant="outline-primary"
                  size="sm"
                  style="height: 40px; width: 40px"
                >
                  <i class="fas fa-plus fa-lg" />
                </b-button>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <v-client-table :data="acl" :columns="columns" :options="options">
                  <template slot="controller" slot-scope="{ row }">
                    <b-badge
                      :variant="getBadgeVariantByAcl(row)"
                      class="text-white ml-2 font-weight-light"
                      style="font-size: 12px"
                    >
                      {{ getAclDescription(row).http_method }}
                    </b-badge>
                    {{ row.controller }}
                  </template>
                  <template slot="action" slot-scope="{ row }">
                    {{ row.action }}
                  </template>

                  <template slot="actions" slot-scope="{ row }">
                    <div class="d-flex flex-row flex-nowrap">
                      <b-button
                        @click="deleteAcl(row)"
                        variant="outline-primary"
                        style="height: 34px; width: 34px"
                        class="ml-2 border-0 p-1"
                      >
                        <i class="far fa-trash-alt fa-lg" />
                      </b-button>
                    </div>
                  </template>

                  <template v-key="row.controller" v-slot:child_row="{ row }">
                    <div>
                      <b>Method:</b>
                      <b-badge
                        :variant="getBadgeVariantByAcl(row)"
                        class="text-white ml-2 font-weight-light"
                        style="font-size: 12px"
                      >
                        {{ getAclDescription(row).http_method }}
                      </b-badge>
                    </div>
                    <div><b>Controller:</b> {{ row.controller }}</div>
                    <div><b>Action:</b> {{ row.action }}</div>
                    <div><b>Pattern:</b> {{ getAclDescription(row).pattern }}</div>
                  </template>
                </v-client-table>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col class="d-flex justify-content-end">
              <b-button variant="outline-secondary" class="mr-2 mb-2" @click="$emit('delete')">
                {{
                  serviceAccount.suspended || serviceAccount.api_key === ''
                    ? $t('button.delete')
                    : $t('button.suspend')
                }}
              </b-button>
              <b-button class="mb-2" @click="saveServiceAccount">
                {{ $t('button.save') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-row>
</template>

<script>
import fields from '../resourcesEditingFields';
import { differenceWith, filter } from 'lodash';
import ResourcesView from '../ResourcesView.vue';

export default {
  name: 'ServiceAccountSettings',
  props: {
    serviceAccount: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      fields: fields,
      columns: ['controller', 'action', 'actions'],
      options: {
        skin: 'table-p-1',
        headings: {
          controller: 'Controller',
          action: 'Action',
          actions: '',
        },
        sortable: [],
        filterable: false,
        perPage: 100,
        perPageValues: [],
      },
      fullAcl: [],
      fullAclObject: {},
      acl: [],
      chosenController: '',
      chosenAction: '',
      VARIANT_BY_METHOD: Object.freeze({
        GET: 'success',
        POST: 'warning',
        PUT: 'dark',
        DELETE: 'danger',
      }),
    };
  },
  computed: {
    availableAcl() {
      let aclLeft = differenceWith(this.fullAcl, this.acl, this.isAclEqual);
      return this.aclArrayToObject(aclLeft);
    },
    availableControllers() {
      return Object.keys(this.availableAcl);
    },
    availableActions() {
      return this.availableAcl[this.chosenController]
        ? Object.keys(this.availableAcl[this.chosenController])
        : [];
    },
  },
  mounted() {
    this.fetchAccountAcl();
    this.fetchFullAcl();
  },
  methods: {
    isAclEqual(acl1, acl2) {
      return acl1.controller == acl2.controller && acl1.action === acl2.action;
    },
    addAcl() {
      this.acl.push({
        id: Date.now(),
        controller: this.chosenController,
        action: this.chosenAction,
      });
      this.chosenController = '';
      this.chosenAction = '';
    },
    deleteAcl(endpoint) {
      this.acl = filter(this.acl, (el) => !this.isAclEqual(el, endpoint));
    },
    getAclDescription(endpoint) {
      if (this.fullAclObject && this.fullAclObject[endpoint.controller]) {
        return this.fullAclObject[endpoint.controller][endpoint.action];
      }
      return {};
    },
    fetchAccountAcl() {
      return this.getData(`resources/service-accounts/${this.serviceAccount.id}/acl`).then(
        (response) => {
          if (response) {
            const { data } = response;
            this.acl = data.items;
          }
        },
      );
    },
    aclArrayToObject(aclArray) {
      let aclObject = {};
      aclArray.forEach((endpoint) => {
        aclObject[endpoint.controller] = aclObject[endpoint.controller] || [];
        aclObject[endpoint.controller][endpoint.action] = {
          http_method: endpoint.http_methods,
          pattern: endpoint.pattern,
        };
      });
      return aclObject;
    },
    fetchFullAcl() {
      return this.getData(`resources/service-accounts/acl`).then((response) => {
        if (response) {
          const { data } = response;
          const fullAcl = data.items;

          this.fullAcl = fullAcl;
          this.fullAclObject = this.aclArrayToObject(fullAcl);
        }
      });
    },
    getBadgeVariantByAcl(acl){
      return this.VARIANT_BY_METHOD[this.getAclDescription(acl).http_method];
    },
    async updateServiceAccount() {
      await this.putData(
        `resources/service-accounts/${this.serviceAccount.id}`,
        {
          ...this.serviceAccount,
        },
        null,
        true,
      )
        .then(() => {
          this.triggerUpdate();
        })
        .catch((error) => {
          console.log('update service account error', error);
          this.$noty.error(error.response.data.error_description);
        });

      await this.putData(`resources/service-accounts/${this.serviceAccount.id}/acl`, {
        items: [...this.acl],
      })
        .then(() => {
          this.triggerUpdate();
        })
        .catch((error) => {
          console.log('update acl error', error);
        });
    },
    createServiceAccount() {
      this.postData(
        `resources/service-accounts`,
        {
          ...this.serviceAccount,
        },
        null,
        true,
      )
        .then(({ data }) => {
          const newServiceAccount = data;
          this.putData(`resources/service-accounts/${newServiceAccount.id}/acl`, {
            items: [...this.acl],
          }).then(() => {
            this.triggerUpdate();
          });
        })
        .catch((error) => {
          console.log('update service account settings error', error);
          this.$noty.error(error.response.data.error_description);
        });
    },
    saveServiceAccount() {
      this.loading = true;
      if (this.serviceAccount.api_key !== '') {
        this.updateServiceAccount();
      } else {
        this.createServiceAccount();
      }
    },
    triggerUpdate() {
      this.$emit('update');
      this.$forceUpdate();
    },
  },
  components: { ResourcesView },
};
</script>
